export default {
  id: 'intraday-scanners',
  title: 'Intraday Scanners',
  content: `## Intraday Scanners

Intraday scanners run every 5 and 15 minutes during market hours on 1-minute and 5-minute candle data. They detect setups suitable for same-day trades.

Default intraday targets: **+2% from entry**. Default stop: **−1% from entry**. R:R: 2:1.

---

## ORB_BREAKOUT — Opening Range Breakout

**Trigger:** Close breaks above the high of the first 15 minutes of trading (the Opening Range), with volume > 1.3× the session average.

What it means: The first 15 minutes sets the reference range. A break above it on volume signals that buyers are in control for the day.

> The Opening Range Breakout is one of the most widely used intraday strategies. The key filter is volume — a break without volume often fails within the hour.

When it fails: If price breaks above the OR high but volume is low or price immediately returns below the OR high, the breakout is false.

---

## GAP_UP_HOLD

**Trigger:** Stock opens more than 1% above the prior close (gap up) and is still holding above the open price after 20 minutes of trading.

What it means: The gap was not immediately sold into. Buyers are absorbing early selling. A held gap typically continues in the gap direction through the session.

What to avoid: Gaps that open and immediately reverse within the first 5 minutes are gap-and-trap setups — the opposite.

---

## INTRADAY_REVERSAL

**Trigger:** Price drops more than 1.5% from the open, then recovers and crosses back above VWAP.

What it means: An intraday panic sell was absorbed by buyers at lower prices. The recovery above VWAP signals that the session bias has flipped back to bullish.

Best when: The initial drop is on declining volume and the recovery is on rising volume.

---

## VWAP_RECLAIM

**Trigger:** Price was below VWAP for 25 of the last 30 candles, then closes above VWAP.

What it means: After an extended period of selling pressure (sellers in control = price below VWAP), buyers have reclaimed the key intraday reference level. A momentum shift signal.

---

## INTRADAY_MOMENTUM

**Trigger:** Three consecutive bullish candles, each with higher volume than the previous.

What it means: Sustained buying across three consecutive minutes with accelerating volume. Short-term momentum continuation signal.

Best when: Occurs in the direction of the overall session trend (price above VWAP).

---

## BB_SQUEEZE_BREAK

**Trigger:** The last 10 candles had a price range < 0.5% (Bollinger Band squeeze on 1-min), then the current candle breaks above that range with volume > 1.5× average.

What it means: A compression of intraday volatility followed by a directional release. Squeezes release energy quickly — the break direction often has strong follow-through.

> The direction of the break matters. A squeeze does not predict direction — only that a move is coming.

---

## SUPPORT_BOUNCE

**Trigger:** Price comes within 0.3% of the prior day's close (key intraday support reference), then bounces with volume > 1.5× average.

What it means: The prior close is watched by institutional intraday traders as a reference. A bounce off it with volume suggests strong buyer defence.

---

## PREV_HIGH_BREAKOUT

**Trigger:** Price closes above the prior day's high with volume > 1.3× average.

What it means: The stock has exceeded all of yesterday's trading range. The prior day's high is a natural short-term resistance level — clearing it with volume signals continuation.

---

## GAP_DOWN_REVERSAL

**Trigger:** Stock opens more than 1% below prior close (gap down), then recovers and closes above the prior day's close within the session.

What it means: A full gap reversal. The initial weakness was completely absorbed. Strong intraday demand signal.

---

## How Intraday Differs from Swing

| Dimension | Intraday | Swing |
|---|---|---|
| Data | 1-min / 5-min candles | Daily candles |
| Decision speed | Fast | Analytical |
| Outcome window | Same day | Up to 15 days |
| Position type | INTRADAY (auto-exit 3:10 PM) | DELIVERY (overnight) |
| Key reference levels | VWAP, Opening Range, prior close | SMA20, SMA50, 52W levels |`,
};
